/**
 * IMPORTS
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ISincronizarCamposVariaveisVariables } from "@/hooks/modelos/sincronizar-campos-variaveis-modelo/interface";

// typings
import type { ISincronizarCamposVariaveisModeloResponseApi } from "@/data/dtos/response-api/modelos/sincronizar-campos-variaveis-modelo-response-api";
// factories
import { makeSincronizarCamposVariaveisModeloUseCase } from "@/factories/modelos/make-sincronizar-campos-variaveis-modelo-factory";
// utils
import { getApiErrorMessage } from "@/utils/normalize-api-error/normalize-api-error";
import { notifyError, notifySuccess } from "@/utils/notify-messages";

/**
 * Hook React Query — PUT sincronização de campos variáveis por modelo técnico.
 */
function useSincronizarCamposVariaveisModelo() {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    ISincronizarCamposVariaveisModeloResponseApi,
    Error,
    ISincronizarCamposVariaveisVariables
  >({
    mutationFn: ({ modeloTecnicoId, payload }) =>
      makeSincronizarCamposVariaveisModeloUseCase().execute(modeloTecnicoId, payload),

    onSuccess: async (response) => {
      if (response?.status === "success") {
        notifySuccess(response.message ?? "Campos variáveis sincronizados com sucesso!");

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["modelos-por-cliente"] }),
          queryClient.invalidateQueries({ queryKey: ["modelos"] }),
          queryClient.invalidateQueries({ queryKey: ["modelos-unico"] }),
        ]);
        return;
      }
      notifyError("Resposta inesperada ao sincronizar campos variáveis.");
    },

    onError: (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });

  return {
    sincronizarCamposVariaveisModelo: mutation.mutate,
    sincronizarCamposVariaveisModeloAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}

/**
 * EXPORT
 */
export { useSincronizarCamposVariaveisModelo };
